import logger from './logger';
import React, { useState, useEffect, useContext } from "react";
import DatePicker from 'react-datepicker';
import { convertUTCToCentral, getUserId } from './utils';
import "../style/ScheduleModal.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-datepicker/dist/react-datepicker.css';
import { GlobalStateContext } from './GlobalStateContext';
import { convertUTCToLocal, convertLocalToUTC } from './utils';


// Clinic hours — appointments only run 9am-6pm, so the time picker shouldn't
// offer anything outside that window (react-datepicker's minTime/maxTime only
// look at the hours/minutes, not the date, so any day works as the carrier).
const APPOINTMENT_MIN_TIME = new Date();
APPOINTMENT_MIN_TIME.setHours(9, 0, 0, 0);
const APPOINTMENT_MAX_TIME = new Date();
APPOINTMENT_MAX_TIME.setHours(18, 0, 0, 0);

const api = {
  getExams: async (visitType) => {
    const exams = {
      'Clinic': ['Consultation', 'Follow-up'],
      'Radiology': ['X-Ray', 'US', 'MRI', 'CT', 'DEXA'],
      'Lab': ['Blood', 'Urine', 'Stool', 'Plasma'],
      'Physical': ['Physical Exam'],
      'Procedure': ['Suture', 'Incision', 'Injection' ]
    };
    return (exams[visitType] || []).map(name => ({ id: name, name }));
  }
}

export default function ScheduleModal({ patient, onSubmit }) {
  const [form, setForm] = useState({
    // visitType: "",
    appointmentDate: null,
    currentDate: new Date(),
    // exam: "",
    phone_number: patient.primary_phone,
    email: patient.email,
    dob: patient.dob,
    bill_insurance: false,
  });

  // logger.log("Passed info: ", patient)

  // ✅ Same lists you used in your scheduling component
  const visitTypes = [
    "Clinic",
    "Radiology",
    "Lab",
    "Physical",
    "Procedure",
  ];

  // Full body MRI exams run long — auto-block a 2-hour slot, since exam_durations
  // keys off modality alone and can't tell a full body MRI from any other MRI.
  const isFullBodyMRI = patient?.type === 'MRI' &&
    /full\s*body/i.test(patient?.exam_ordered || '');

  // null means "no explicit choice" — the appointment then inherits whatever
  // duration is configured in exam_durations. Only a deliberate pick (or the
  // full body default above) is sent as an override, so the configured
  // duration is never silently replaced by a hardcoded one.
  const [mriDuration, setMriDuration] = useState(isFullBodyMRI ? 120 : null);
  const [configuredDuration, setConfiguredDuration] = useState(null);

  // What this booking will actually block, for display on the toggle.
  const effectiveDuration = mriDuration ?? configuredDuration;

  // Contrast CT's take 60 minutes instead of the standard 30 — default the toggle
  // from the ordered exam name (e.g. "CT chest with contrast", "CT sinus w/o and
  // w/ contrast") but let staff override it manually either way.
  const examMentionsContrast = /w\/o and w\/ contrast|w\/\s*contrast\b|with contrast/i.test(patient?.exam_ordered || '');
  const [ctContrast, setCtContrast] = useState(patient?.type === 'CT' && examMentionsContrast);
  const [bookedSlots, setBookedSlots] = useState([]);
  const BASE_URL = process.env.REACT_APP_API_URL;
  const [exams, setExams] = useState([]);
  const {currentUser} = useContext(GlobalStateContext);
  const user = getUserId(currentUser)

  // Keyed by 'YYYY-MM-DD' -> { clinicClosed, blockedModalities, allLabsBlocked,
  // blockedSpecimens } — merges clinic-wide closures (carried over from Health
  // Hub's AdminBlockedDates) with the modality/lab equipment+staffing gaps the
  // backend already enforces at POST /scheduling/orderSchedule, so the date
  // picker can't offer a day the booking would be rejected on.
  const [unavailableDates, setUnavailableDates] = useState({});

  const fetchUnavailableDates = async (monthDate) => {
    try {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const [closureRes, modalityRes, labRes] = await Promise.all([
        fetch(`${BASE_URL}/scheduling/clinicClosures?startDate=${startDate}&endDate=${endDate}`),
        fetch(`${BASE_URL}/scheduling/modalityAvailability?startDate=${startDate}&endDate=${endDate}`),
        fetch(`${BASE_URL}/scheduling/labAvailability?startDate=${startDate}&endDate=${endDate}`),
      ]);
      const closures = closureRes.ok ? await closureRes.json() : [];
      const modalityData = modalityRes.ok ? await modalityRes.json() : {};
      const labData = labRes.ok ? await labRes.json() : {};

      setUnavailableDates(prev => {
        const merged = { ...prev };
        closures.forEach(c => {
          const d = c.blocked_date.split('T')[0];
          merged[d] = { ...(merged[d] || {}), clinicClosed: true };
        });
        Object.keys(modalityData).forEach(d => {
          merged[d] = { ...(merged[d] || {}), blockedModalities: modalityData[d].blockedModalities };
        });
        Object.keys(labData).forEach(d => {
          merged[d] = {
            ...(merged[d] || {}),
            allLabsBlocked: labData[d].allBlocked,
            blockedSpecimens: labData[d].blockedSpecimens,
          };
        });
        return merged;
      });
    } catch (error) {
      logger.error("Error fetching unavailable dates", error);
    }
  };

  useEffect(() => {
    fetchUnavailableDates(form.currentDate);
  }, []);

  // Why (if at all) `date` can't be booked for this order — clinic closed, or
  // (for Radiology/Lab) nobody available to run the exam/modality that day.
  // Returns null when the date is bookable.
  const getDateUnavailableReason = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const info = unavailableDates[dateStr];
    if (!info) return null;
    if (info.clinicClosed) return 'Clinic closed';
    if (patient?.order_type === 'Radiology' && info.blockedModalities?.includes(patient?.type)) {
      return `No rad tech available to run ${patient.type}`;
    }
    if (patient?.order_type === 'Lab') {
      if (info.allLabsBlocked) return 'No lab-capable staff available';
      if (info.blockedSpecimens?.includes(patient?.type)) return `No staff available to run ${patient.type} labs`;
    }
    return null;
  };
  const isDateUnavailable = (date) => getDateUnavailableReason(date) !== null;

  const fetchBookedSlots = async (visit_type, exam, selectedDate) => {
    try {
      const dateStr = selectedDate 
        ? selectedDate.toISOString().split('T')[0] 
        : new Date().toISOString().split('T')[0];
      
      const response = await fetch(
        `${BASE_URL}/scheduling/bookedSlotsWorklist?visit_type=${visit_type}&exam=${exam}&date=${dateStr}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const excludedTimes = [];
          
          data.forEach((slot) => {
            // Use helper function to convert UTC to local
            const startTime = convertUTCToLocal(slot.appointment_start);
            const endTime = convertUTCToLocal(slot.appointment_end);
            
            let currentTime = new Date(startTime);
            while (currentTime < endTime) {
              excludedTimes.push(new Date(currentTime));
              const intervalMinutes = visit_type === "Radiology" ? 30 : 10;
              currentTime = new Date(currentTime.getTime() + intervalMinutes * 60000);
            }
          });
          
          setBookedSlots(excludedTimes);
        } else {
          setBookedSlots([]);
        }
      }
    } catch (error) {
      logger.error("Error fetching booked slots", error);
      toast.error("Error fetching available slots");
    }
  };

  // Initial load - fetch booked slots for current date
  useEffect(() => {
    if (patient?.order_type && patient?.order_type !== 'Lab' && patient?.type) {
      fetchBookedSlots(patient?.order_type, patient?.type, form.currentDate);
    }
  }, [patient?.order_type, patient?.type]);

  // Refetch when user selects a date
  useEffect(() => {
    if (form.appointmentDate && patient?.order_type !== 'Lab' && patient?.type) {
      fetchBookedSlots(patient?.order_type, patient?.type, form.appointmentDate);
    }
  }, [form.appointmentDate?.toDateString()]);

  // Pull the configured duration so the toggle can show how much time this
  // booking blocks by default, instead of staff having to assume it.
  useEffect(() => {
    if (patient?.type !== 'MRI' || !patient?.order_type) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/scheduling/examDuration?exam_type=${encodeURIComponent(patient.type)}&visit_type=${encodeURIComponent(patient.order_type)}`
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setConfiguredDuration(data?.duration_minutes ?? null);
      } catch (error) {
        logger.error("Error fetching configured exam duration", error);
      }
    })();

    return () => { cancelled = true; };
  }, [patient?.type, patient?.order_type]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Add validation check
    if (!form.appointmentDate) {
      toast.error("Please select an appointment date and time");
      return;
    }

    try {
      const postBody = {
        ris_patients_id: patient?.ris_patients_id || null,
        appointment_start: convertLocalToUTC(form.appointmentDate),
        email: patient?.email,
        first_name: patient?.first_name,
        last_name: patient?.last_name,
        phone_number: patient?.primary_phone,
        user: user || null,
        type: currentUser?.type || null,
        order_type: patient?.order_type,
        order_id: patient?.order_id,
        order_exam: patient?.type, // Modality for duration lookup
        // null unless staff picked a length (or this is a full body MRI/contrast CT), so the
        // backend falls through to the configured exam_durations value.
        duration_override: patient?.type === 'MRI'
          ? mriDuration
          : (patient?.type === 'CT' && ctContrast ? 60 : null),
        bill_insurance: form.bill_insurance,
      };

      const response = await fetch(`${BASE_URL}/scheduling/orderSchedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(postBody)
      });

      if (response.status === 201) {
        const data = await response.json();
        toast.success("Appointment scheduled successfully!");
        // Wait for toast to display before closing modal and refreshing
        setTimeout(() => {
          document.getElementById("schedule-modal").classList.remove("show");
          if (onSubmit) onSubmit(form);
        }, 1500);
      } else {
        const err = await response.json();
        logger.error("Failed scheduling:", err);
        toast.error("Error: " + (err.error || err.msg || "Could not schedule appointment"));
      }
    } catch (error) {
      logger.error("Error submitting appointment:", error);
      toast.error("Server error scheduling appointment");
    }
  };

  return (
    <div className="order-modal-container">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <button
        className="order-open-modal-btn"
        onClick={() =>
          document.getElementById("schedule-modal").classList.add("show")
        }
      >
        Schedule Appointment
      </button>

      <div id="schedule-modal" className="order-modal">
        <div className="order-modal-content">
          <h2>Schedule Appointment</h2>
          <form onSubmit={handleSubmit} className="order-form">
            {/* Visit Type Dropdown */}
            <div className="order-form-group">
              <label>Visit Type: </label>
              <span>{patient?.order_type}</span>
            </div>

            {/* Exam Dropdown */}
            <div className="order-form-group">
              <label>Exam: </label>
              <span>{patient?.type}</span>
            </div>

            {/* Referring Provider */}
            {patient?.referring_provider && (
              <div className="order-form-group">
                <label>Referring Provider: </label>
                <span>{patient.referring_provider}</span>
              </div>
            )}

            {/* MRI Duration Toggle */}
            {patient?.type === 'MRI' && (
              <div className="order-form-group">
                <label>Appointment Duration</label>
                <div className="mri-duration-toggle">
                  {[60, 90, 120].map((minutes) => (
                    <button
                      key={minutes}
                      type="button"
                      className={`mri-duration-btn${effectiveDuration === minutes ? ' active' : ''}`}
                      onClick={() => setMriDuration(minutes)}
                    >
                      {minutes} min
                    </button>
                  ))}
                </div>
                <span className="mri-duration-hint">
                  {isFullBodyMRI
                    ? 'Full body MRI blocks 2 hours'
                    : mriDuration !== null
                      ? `Blocking ${mriDuration} minutes`
                      : configuredDuration
                        ? `Blocking ${configuredDuration} minutes — the configured default for MRI. Choose another to change it.`
                        : 'Blocking the configured default for MRI. Choose another to change it.'}
                </span>
              </div>
            )}

            {/* CT Contrast Toggle */}
            {patient?.type === 'CT' && (
              <div className="order-form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={ctContrast}
                    onChange={e => setCtContrast(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#0e8a8f' }}
                  />
                  <span>Contrast (blocks a 60 minute slot)</span>
                </label>
              </div>
            )}

            {/* Appointment Date */}
            <div className="order-form-group">
              <label>Appointment Date</label>
              <span className="schedule-date-legend">Greyed-out dates are unavailable — clinic closed or no staff/equipment to perform this exam.</span>
                <DatePicker
                  selected={form.appointmentDate}
                  className="date-picker"
                  timeIntervals={
                    patient.order_type === "Radiology"
                      // Ultrasound books on-the-hour, 60 minute slots; other radiology
                      // modalities (including contrast CT, which uses duration_override
                      // above rather than the slot grid) stay on the 30 minute grid.
                      ? (patient.type === 'US' ? 60 : 30)
                      : 10
                  }
                  dateFormat="MMMM d, yyyy h:mm aa"
                  onChange={(value) => {
                    setForm({ ...form, appointmentDate: value });
                  }}
                  onMonthChange={fetchUnavailableDates}
                  minDate={new Date()}
                  showTimeSelect
                  minTime={APPOINTMENT_MIN_TIME}
                  maxTime={APPOINTMENT_MAX_TIME}
                  filterDate={(date) => !isDateUnavailable(date)}
                  dayClassName={(date) => getDateUnavailableReason(date) ? 'schedule-day-unavailable' : undefined}
                  renderDayContents={(dayOfMonth, date) => {
                    const reason = date ? getDateUnavailableReason(date) : null;
                    return <span title={reason || undefined}>{dayOfMonth}</span>;
                  }}
                  filterTime={(time) => {
                    // Check if this time conflicts with any booked slot
                    const isBooked = bookedSlots.some(slot => {
                      const selectedDateStr = (form.appointmentDate || form.currentDate)?.toDateString();
                      const slotDateStr = slot.toDateString();

                      // Only compare times on the same day
                      if (selectedDateStr !== slotDateStr) return false;

                      // Compare hours and minutes
                      return time.getHours() === slot.getHours() &&
                            time.getMinutes() === slot.getMinutes();
                    });
                    return !isBooked;
                  }}
                />
            </div>

            {/* Bill Insurance — not applicable for Lab orders */}
            {patient?.order_type !== 'Lab' && (
              <div className="order-form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={form.bill_insurance}
                    onChange={e => setForm({ ...form, bill_insurance: e.target.checked })}
                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#0e8a8f' }}
                  />
                  <span>Bill Insurance</span>
                </label>
              </div>
            )}

            <button type="submit" className="order-submit-btn">
              Save Appointment
            </button>
          </form>

          <button
            className="order-close-btn"
            onClick={() =>
              document.getElementById("schedule-modal").classList.remove("show")
            }
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}